/**
 * <tecton-qty> — quantity stepper.
 *
 * Respects the variant's quantity rules (min / max / increment) rather than
 * stepping by one blindly, and dispatches a `change` event so the surrounding
 * product form and any listening app pick the value up.
 *
 * Also handles being switched off. Bundler and similar apps take over quantity
 * with their own tier selector ("1 Box / 2 Boxes / …") and hide the theme's
 * input inline. Left alone that produces a stepper with two buttons and no
 * number between them — visibly broken. When the input is hidden by something
 * other than this component, the whole control hides with it.
 */

class TectonQty extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[name="quantity"]');
    this.down = this.querySelector('[data-qty-down]');
    this.up = this.querySelector('[data-qty-up]');
    if (!this.input) return;

    this.down?.addEventListener('click', () => this.step(-1));
    this.up?.addEventListener('click', () => this.step(1));
    this.input.addEventListener('change', () => this.clamp());

    this.watchForAppTakeover();
    this.sync();
  }

  disconnectedCallback() {
    this.watcher?.disconnect();
  }

  get min() {
    return Number(this.input.min) || 1;
  }

  get max() {
    const max = Number(this.input.max);
    return Number.isFinite(max) && max > 0 ? max : Infinity;
  }

  get increment() {
    return Number(this.input.step) || 1;
  }

  step(direction) {
    const current = Number(this.input.value) || this.min;
    this.input.value = current + direction * this.increment;
    this.clamp();
    // Bubbles so the product form — and any app listening on the form — sees it.
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  clamp() {
    let value = Number(this.input.value);
    if (!Number.isFinite(value)) value = this.min;
    value = Math.min(this.max, Math.max(this.min, value));

    // Keep the value on a valid increment boundary above the minimum.
    const steps = Math.round((value - this.min) / this.increment);
    this.input.value = this.min + steps * this.increment;

    this.sync();
  }

  sync() {
    const value = Number(this.input.value) || this.min;
    if (this.down) this.down.disabled = value <= this.min;
    if (this.up) this.up.disabled = value >= this.max;
  }

  /** Hide the control entirely if an app hides the input it wraps. */
  watchForAppTakeover() {
    const check = () => {
      const hidden = this.input.offsetParent === null && getComputedStyle(this.input).display === 'none';
      this.toggleAttribute('data-app-controlled', hidden);
    };

    check();
    this.watcher = new MutationObserver(check);
    this.watcher.observe(this.input, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
  }
}

if (!customElements.get('tecton-qty')) {
  customElements.define('tecton-qty', TectonQty);
}
